import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { Rate } from "antd";

const buildUuidLabelMap = (categorizedList = []) =>
  categorizedList.reduce((acc, entry) => {
    const [label, uuid] = Object.entries(entry)[0] || [];

    if (uuid) acc[uuid] = label;

    return acc;
  }, {});

const buildRoleUuidLabelMap = (eventRolesByCategory = {}) =>
  Object.values(eventRolesByCategory).reduce((acc, category) => {
    (category?.event_roles || []).forEach((role) => {
      if (role?.uuid) {
        acc[role.uuid] = role.name;
      }
    });

    return acc;
  }, {});

const RoleBasedRatings = ({
  roles = [],
  skills = [],
  resetKey,
  readOnly = false,
  onRoleChange
}) => {
  const [ratings, setRatings] = useState({
    event_roles: [],
    skills: []
  });

  const [hoveredValues, setHoveredValues] = useState({});

  const skillCategorized = useSelector(
    (state) => state.talent.skillCategorized
  );

  const languageCategorized = useSelector(
    (state) => state.talent.languageCategorized
  );

  const eventRolesByCategory = useSelector(
    (state) => state.talent.eventRoles
  );

  const skillLabelMap = useMemo(
    () => ({
      ...buildUuidLabelMap(skillCategorized),
      ...buildUuidLabelMap(languageCategorized)
    }),
    [skillCategorized, languageCategorized]
  );

  const roleLabelMap = useMemo(
    () => buildRoleUuidLabelMap(eventRolesByCategory),
    [eventRolesByCategory]
  );

  useEffect(() => {
    const nextRatings = {
      event_roles: roles.map((item) => ({
        uuid: item.uuid,
        label:
          roleLabelMap[item.uuid] ??
          item.label ??
          item.uuid,
        value: item.value ?? null
      })),

      skills: skills.map((item) => ({
        uuid: item.uuid,
        label:
          skillLabelMap[item.uuid] ??
          item.label ??
          item.uuid,
        value: item.value ?? null
      }))
    };

    setRatings(nextRatings);
    onRoleChange?.(nextRatings);
  }, [
    roles,
    skills,
    roleLabelMap,
    skillLabelMap
  ]);

  useEffect(() => {
    if (!resetKey) return;

    const defaultRatings = {
      event_roles: [],
      skills: []
    };

    setRatings(defaultRatings);
    setHoveredValues({});

    onRoleChange?.(defaultRatings);
  }, [resetKey]);

  const handleRatingChange = (type, item, value) => {
    const updatedRatings = {
      ...ratings,

      [type]: ratings[type].map((rating) =>
        rating.uuid === item.uuid
          ? {
            ...rating,
            value
          }
          : rating
      )
    };

    setRatings(updatedRatings);
    onRoleChange?.(updatedRatings);
  };

  const renderRatingItems = (items, type) =>
    items.map((item) => {
      const displayValue =
        hoveredValues[item.uuid] ?? item.value;

      return (
        <div
          key={item.uuid}
          className="flex items-center justify-between"
        >
          <span className="text-sm">
            {item.label}
          </span>

          <div className="flex items-center">
            {displayValue > 0 && (
              <span className="text-sm font-semibold text-blue-600 mr-2">
                {displayValue}
              </span>
            )}

            <Rate
              allowHalf
              disabled={readOnly}
              value={item.value || 0}
              onChange={(value) =>
                handleRatingChange(type, item, value)
              }
              onHoverChange={(value) =>
                setHoveredValues((prev) => ({
                  ...prev,
                  [item.uuid]: value
                }))
              }
            />
          </div>
        </div>
      );
    });

  return (
    <div className="bg-white space-y-4">
      <h3 className="font-semibold">
        Role-Specific Skills

        <span className="text-xs font-normal text-gray-500">
          &nbsp;(Rate only what you directly observed. Leave anything unknown blank.)
        </span>
      </h3>

      {ratings.event_roles.length || ratings.skills.length ? (
        <>
          {renderRatingItems(
            ratings.event_roles,
            "event_roles"
          )}

          {renderRatingItems(
            ratings.skills,
            "skills"
          )}
        </>
      ) : (
        <div className="text-sm text-gray-500">
          No role-specific ratings are available for this event.
        </div>
      )}
    </div>
  );
};

export default RoleBasedRatings;